import { DismissButton, usePopover } from '@react-aria/overlays';
import { forwardRef, PropsWithChildren } from 'react';
import { useHover } from '@react-aria/interactions';
import { mergeProps } from '@react-aria/utils';
import { Dialog } from 'react-aria-components';
import { FocusScope } from '@react-aria/focus';

import { ButtonContextProvider } from '../Button';
import { useUIString } from '../services/content/hooks/useUIString';

import styles from './Popover.module.scss';

export type DialogContentProps = {
  'aria-label': string;
  onHoverStart: () => void;
  onHoverEnd: () => void;
  onDismiss: () => void;
  onButtonPress: () => void;
  popoverProps: ReturnType<typeof usePopover>['popoverProps'];
  'data-testid'?: string;
};

export const DialogContent = forwardRef<HTMLDivElement, PropsWithChildren<DialogContentProps>>(
  ({ ['aria-label']: ariaLabel, onHoverStart, onHoverEnd, onDismiss, onButtonPress, popoverProps, ['data-testid']: testId, children }, ref) => {
    const dialogDismissAriaLabel = useUIString('dialogDismissAriaLabel');

    const { hoverProps } = useHover({
      onHoverStart,
      onHoverEnd,
    });

    return (
      <FocusScope restoreFocus contain={false}>
        <div {...mergeProps(popoverProps, hoverProps, { className: styles.dialogWrapper })} ref={ref} data-testid={testId}>
          <Dialog aria-label={ariaLabel} className={styles.dialog}>
            <DismissButton onDismiss={onDismiss} aria-label={dialogDismissAriaLabel} />
            <ButtonContextProvider value={{ onPress: onButtonPress }}>{children}</ButtonContextProvider>
          </Dialog>
        </div>
      </FocusScope>
    );
  }
);
